import {
  blockDisplayFromNodeData,
  CAPTURE_LIVE_LIMIT,
} from "@cohesive/ui-components";
import type { Node } from "@xyflow/react";
import { createContext, useContext } from "react";

/**
 * How the capture page's cards treat their documents: rendered live, with
 * the page's document cap (`"capture"`), or posters only (`"poster"`). Read
 * only on the capture variant.
 */
export const CaptureMountPolicyContext = createContext<"capture" | "poster">(
  "capture",
);

export function useCaptureMountPolicy(): "capture" | "poster" {
  return useContext(CaptureMountPolicyContext);
}

const DOCUMENT_NAME = /\.(html?|md|markdown)$/i;

/** HTML blocks (chromeless included) and HTML / markdown document wells. A
 *  well is known only by its file name until its row loads; a miss cannot
 *  break the bound, which the capture scheduler enforces on its own. */
function isLiveCapable(node: Node): boolean {
  if (blockDisplayFromNodeData(node.data)?.mode === "block") return true;
  const entity = (node.data as { entity?: { base?: string; data?: unknown } })
    ?.entity;
  if (entity?.base !== "file_record") return false;
  const { title, path } = (entity.data ?? {}) as {
    title?: string;
    path?: string;
  };
  return DOCUMENT_NAME.test(path ?? title ?? "");
}

/** Live up to `CAPTURE_LIVE_LIMIT` live-capable nodes. A region always
 *  renders live and the scheduler stops at the limit, the rest on posters;
 *  a whole canvas past the limit is all posters. */
export function captureMountPolicyFor(
  nodes: Node[],
  region: boolean,
): "capture" | "poster" {
  return region || nodes.filter(isLiveCapable).length <= CAPTURE_LIVE_LIMIT
    ? "capture"
    : "poster";
}
